import React from 'react';
import {
  BodyText,
  Heading,
  MinorHeading
} from '../../components/shared/typography/PrimaryTypography';
import { Box, Grid, Theme, useTheme } from '@mui/material';
import ImportTypeSelect, { ImportType } from '../../components/data_import/ImportTypeSelect';
import ImportDataGrid from '../../components/data_import/data_grid/ImportDataGrid';
import ImportDataButton, { ImportContext } from '../../components/data_import/ImportDataButton';
import { UserContext } from '../../context/UserContext';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

/**
 * View for importing data into Syntiro of various entities from various sources.
 * @returns {JSX.Element}
 */
const DataImport = () => {
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Sustainability', 'Data Import', group.id, company.id);

  const [access, setAccess] = React.useState<ViewAccess>(null);
  const [importType, setImportType] = React.useState<ImportType | null>(null);
  const [importContext, setImportContext] = React.useState<ImportContext>({});
  const [contextValid, setContextValid] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  const handleContextChange = (new_context: ImportContext): void => {
    setImportContext(new_context);
  };

  const handleContextValidChange = (valid: boolean): void => {
    setContextValid(valid);
  };

  React.useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'sustainability.data_import')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box>
      <Box
        sx={{
          textAlign: 'start',
          my: 3,
          mx: -3,
          boxSizing: 'border-box',
          borderBottom: `1px solid ${theme.palette.teal.dark}`
        }}
      >
        <Grid container spacing={2} sx={{ px: 4, mb: 4 }}>
          <Grid size={9} alignSelf={'center'}>
            <Heading weight="Medium">Bring your data into Syntiro</Heading>
          </Grid>
          <Grid size={12} alignSelf={'center'} display={'flex'}>
            <BodyText>
              Save time by managing your Syntiro data in bulk, get started by selecting a common
              import preset or choose a specific import type below.
            </BodyText>
          </Grid>
        </Grid>
        {/* TODO: Add Import Presets as part of future ticket. */}
        {/* <ImportPresetsBar import_presets={import_presets} handlePresetChange={handlePresetChange} /> */}
      </Box>
      <Grid container spacing={4}>
        {(access === 'write' || access === 'admin') && (
          <Grid size={12}>
            <Box sx={{ maxWidth: 600 }}>
              <ImportTypeSelect
                selected_options={importType}
                handleChangeImportType={(new_type: ImportType | null) => setImportType(new_type)}
              />
            </Box>
          </Grid>
        )}
        {importType && (
          <Grid size={12} sx={{ textAlign: 'left' }}>
            <MinorHeading weight="Medium">
              Add the required context for {importType.name} import
            </MinorHeading>
          </Grid>
        )}
        {importType?.displayContext &&
          importType?.displayContext(handleContextChange, handleContextValidChange)}
        <Grid size={'auto'}>
          {user && importType && (
            <ImportDataButton
              user={user}
              import_type={importType}
              context={importContext}
              disabled={!contextValid}
              label="Import"
            />
          )}
        </Grid>
        <Grid
          size={12}
          sx={{
            textAlign: 'start'
          }}
        >
          <MinorHeading weight="Medium" sx={{ mb: 2 }}>
            Recent Data Imports
          </MinorHeading>
          <ImportDataGrid />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default DataImport;
